// nodeConfigs.js
// Clean, professional node configurations

import { useStore } from "./store";

const createTextField = (
  label,
  fieldName,
  isTextArea = false,
  placeholder = ""
) => ({
  render: (data, id, setDimensions) => {
    const { updateNodeField } = useStore.getState();

    const handleChange = (e) => {
      const value = e.target.value;
      updateNodeField(id, fieldName, value);

      if (isTextArea && setDimensions) {
        const lines = value.split("\n").length;
        const chars = value.length;
        const newWidth = Math.max(220, Math.min(400, chars * 1.5 + 120));
        const newHeight = Math.max(100, lines * 22 + 85);
        setDimensions({ width: newWidth, height: newHeight });
      }
    };

    const labelStyle = {
      fontSize: "11px",
      fontWeight: "500",
      color: "#64748b",
      marginBottom: "6px",
      display: "block",
      textTransform: "uppercase",
      letterSpacing: "0.03em",
    };

    const inputStyle = {
      width: "90%",
      padding: "8px 10px",
      borderRadius: "6px",
      border: "1px solid #e2e8f0",
      background: "#f8fafc",
      color: "#1e293b",
      fontSize: "13px",
      fontFamily: "inherit",
      outline: "none",
      transition: "all 0.15s ease",
    };

    const inputFocusStyle = `
      background: #ffffff;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08);
    `;

    return (
      <div>
        <label style={labelStyle}>{label}</label>
        {isTextArea ? (
          <textarea
            value={data[fieldName] || ""}
            onChange={handleChange}
            placeholder={placeholder}
            style={{
              ...inputStyle,
              minHeight: "70px",
              resize: "none",
              fontFamily: "monospace",
            }}
            onFocus={(e) => (e.target.style.cssText += inputFocusStyle)}
            onBlur={(e) => {
              e.target.style.background = "#f8fafc";
              e.target.style.borderColor = "#e2e8f0";
              e.target.style.boxShadow = "none";
            }}
          />
        ) : (
          <input
            type="text"
            value={data[fieldName] || ""}
            onChange={handleChange}
            placeholder={placeholder}
            style={inputStyle}
            onFocus={(e) => (e.target.style.cssText += inputFocusStyle)}
            onBlur={(e) => {
              e.target.style.background = "#f8fafc";
              e.target.style.borderColor = "#e2e8f0";
              e.target.style.boxShadow = "none";
            }}
          />
        )}
      </div>
    );
  },
});

const createSelectField = (label, fieldName, options) => ({
  render: (data, id) => {
    const { updateNodeField } = useStore.getState();

    const labelStyle = {
      fontSize: "11px",
      fontWeight: "500",
      color: "#64748b",
      marginBottom: "6px",
      display: "block",
      textTransform: "uppercase",
      letterSpacing: "0.03em",
    };

    const selectStyle = {
      width: "100%",
      padding: "8px 10px",
      borderRadius: "6px",
      border: "1px solid #e2e8f0",
      background: "#f8fafc",
      color: "#1e293b",
      fontSize: "13px",
      outline: "none",
      cursor: "pointer",
      transition: "all 0.15s ease",
    };

    return (
      <div>
        <label style={labelStyle}>{label}</label>
        <select
          value={data[fieldName] || options[0]}
          onChange={(e) => updateNodeField(id, fieldName, e.target.value)}
          style={selectStyle}
          onFocus={(e) => {
            e.target.style.background = "#ffffff";
            e.target.style.borderColor = "#3b82f6";
            e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.08)";
          }}
          onBlur={(e) => {
            e.target.style.background = "#f8fafc";
            e.target.style.borderColor = "#e2e8f0";
            e.target.style.boxShadow = "none";
          }}
        >
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    );
  },
});

export const nodeConfigs = {
  customInput: {
    title: "Input",
    icon: "📥",
    color: "#3b82f6",
    inputs: [],
    outputs: [{ id: "value" }],
    fields: [
      createTextField("Name", "inputName", false, "Enter input name"),
      createSelectField("Type", "inputType", ["Text", "File", "Number"]),
    ],
    defaultSize: { width: 220, height: 140 },
  },

  customOutput: {
    title: "Output",
    icon: "📤",
    color: "#ef4444",
    inputs: [{ id: "value" }],
    outputs: [],
    fields: [
      createTextField("Name", "outputName", false, "Enter output name"),
      createSelectField("Type", "outputType", ["Text", "File", "Image"]),
    ],
    defaultSize: { width: 220, height: 140 },
  },

  llm: {
    title: "LLM",
    icon: "🤖",
    color: "#8b5cf6",
    inputs: [{ id: "system" }, { id: "prompt" }],
    outputs: [{ id: "response" }],
    fields: [
      createSelectField("Model", "model", ["GPT-4", "Claude", "Gemini"]),
      createTextField("Temperature", "temperature", false, "0.7"),
    ],
    defaultSize: { width: 220, height: 140 },
  },

  text: {
    title: "Text",
    icon: "📝",
    color: "#10b981",
    inputs: [],
    outputs: [{ id: "output" }],
    fields: [
      createTextField("Text", "text", true, "Enter text with {{variables}}"),
    ],
    defaultSize: { width: 260, height: 160 },
  },

  filter: {
    title: "Filter",
    icon: "🔍",
    color: "#06b6d4",
    inputs: [{ id: "data" }],
    outputs: [{ id: "filtered" }],
    fields: [
      createTextField("Condition", "condition", false, "value > 5"),
      createSelectField("Mode", "mode", ["Include", "Exclude"]),
    ],
    defaultSize: { width: 220, height: 140 },
  },

  transform: {
    title: "Transform",
    icon: "⚡",
    color: "#f59e0b",
    inputs: [{ id: "input" }],
    outputs: [{ id: "output" }],
    fields: [
      createTextField("Function", "function", true, "x => x.toUpperCase()"),
      createSelectField("Type", "transformType", ["Map", "Reduce", "Filter"]),
    ],
    defaultSize: { width: 240, height: 160 },
  },

  database: {
    title: "Database",
    icon: "💾",
    color: "#6366f1",
    inputs: [{ id: "query" }],
    outputs: [{ id: "result" }],
    fields: [
      createTextField("Connection", "connection", false, "postgres://..."),
      createSelectField("Operation", "operation", [
        "Select",
        "Insert",
        "Update",
        "Delete",
      ]),
    ],
    defaultSize: { width: 240, height: 140 },
  },

  conditional: {
    title: "Conditional",
    icon: "🔀",
    color: "#ec4899",
    inputs: [{ id: "input" }],
    outputs: [{ id: "true" }, { id: "false" }],
    fields: [
      createTextField("Condition", "condition", false, "value > 10"),
      createSelectField("Operator", "operator", [
        ">",
        "<",
        "==",
        "!=",
        ">=",
        "<=",
      ]),
    ],
    defaultSize: { width: 220, height: 140 },
  },

  api: {
    title: "API Call",
    icon: "🌐",
    color: "#84cc16",
    inputs: [{ id: "params" }],
    outputs: [{ id: "response" }],
    fields: [
      createTextField("URL", "url", false, "https://api.example.com"),
      createSelectField("Method", "method", ["GET", "POST", "PUT", "DELETE"]),
      createTextField(
        "Headers",
        "headers",
        true,
        '{"Authorization": "Bearer ..."}'
      ),
    ],
    defaultSize: { width: 260, height: 200 },
  },
};
